/* eslint-disable @typescript-eslint/no-explicit-any */
import { Sender } from 'xstate';
import { reportDegradedLoad } from './helpers/report-degraded-load';

import {
  ProStudioContext,
  ProStudioEvents,
  ProStudioInterpreter,
} from '../prostudio.xstate.types';
import { globalAppService, ldClient } from '../app.xstate';
import { GLOBAL_APP_SUB_MACHINE_IDS } from '../app.xstate-utils';

interface LDClientContext
  extends Pick<
    ProStudioContext['userInfo'],
    'email' | 'defaultOrgId' | 'status'
  > {
  kind: 'user';
  key: ProStudioContext['userInfo']['id'];
}

const throttle = <F extends (...args: any[]) => any>(fn: F, delay = 1000) => {
  let lastCall = 0;
  let pendingArgs: Parameters<F> | null = null;
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>) => {
    const now = Date.now();

    const invoke = () => {
      lastCall = Date.now();
      timeout = null;
      fn(...(pendingArgs || args));
      pendingArgs = null;
    };

    const timeSinceLastCall = now - lastCall;

    if (timeSinceLastCall >= delay) {
      invoke();
    } else {
      pendingArgs = args;

      if (!timeout) {
        timeout = setTimeout(() => {
          invoke();
        }, delay - timeSinceLastCall);
      }
    }
  };
};

export const identifyLaunchDarklyContext =
  (_ctx: ProStudioContext, _event: ProStudioEvents) =>
  async (_sendEvent: Sender<ProStudioEvents>) => {
    let prevLDClientContext: LDClientContext | null = null;

    const proStudioService = globalAppService.children.get(
      GLOBAL_APP_SUB_MACHINE_IDS.proStudio
    ) as ProStudioInterpreter;

    const throttledIdentify = throttle(async (ctx: LDClientContext) => {
      try {
        await ldClient.identify(ctx);
        console.log(`[LaunchDarkly] Identified user: ${ctx.key}`);
      } catch (error) {
        console.error('[LaunchDarkly] Identify failed:', error);
        // Worth reporting rather than logging: every feature flag in the app
        // then keeps answering for the ANONYMOUS context, so a targeted
        // rollout silently misses this user — including the two flags that
        // decide whether Sentry reports at all.
        reportDegradedLoad(error, 'identify-ld-context', {
          contextKey: ctx.key,
        });
      }
    }, 1000);

    const subscription = proStudioService?.subscribe((state) => {
      const prostudioContext = state?.context;
      if (!prostudioContext) return;

      const {
        userInfo: { id: key, email, defaultOrgId, status },
      } = prostudioContext;

      if (!key) {
        return;
      }

      // Here we can add any property from pro-studio context to the LaunchDarkly context.
      const currentLDClientContext: LDClientContext = {
        kind: 'user',
        key,
        email,
        defaultOrgId,
        status,
      };

      const hasChanged =
        !prevLDClientContext ||
        JSON.stringify(prevLDClientContext) !==
          JSON.stringify(currentLDClientContext);

      if (!hasChanged) {
        return;
      }

      prevLDClientContext = currentLDClientContext;

      throttledIdentify(currentLDClientContext);
    });

    return () => {
      subscription?.unsubscribe();
    };
  };
